import { useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { PageLayout } from '@/layouts'
import { Button, Modal, ConfirmModal, LoadingSpinner, ErrorDisplay, Select } from '@/components/ui'
import {
  PrivacyTable,
  PrivacyForm,
  PrivacyDetailModal,
  CreatePrivacyFormData,
  UpdatePrivacyFormData,
} from '@/features/privacy/components'
import {
  usePrivacyListAll,
  useCreatePrivacy,
  useUpdatePrivacy,
  useDeletePrivacy,
} from '@/hooks/use-privacy'
import { PrivacySubject } from '@/models/privacy'
import { useToast } from '@/hooks/use-toast'

// Filter types
type FilterType = 'all' | 'in_progress' | 'completed' | 'pending_verification' | 'due_soon' | 'overdue'

const FILTER_OPTIONS: { value: FilterType; label: string }[] = [
  { value: 'all', label: 'All Subjects' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending_verification', label: 'Pending Verification' },
  { value: 'due_soon', label: 'Due Soon (7 days)' },
  { value: 'overdue', label: 'Overdue' },
]

// Filter logic
function filterSubjects(subjects: PrivacySubject[], filter: FilterType): PrivacySubject[] {
  if (filter === 'all') return subjects

  const now = new Date()
  const sevenDaysFromNow = new Date(now)
  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7)

  return subjects.filter((subject) => {
    // Skip completed or denied for due date filters
    const isActive = subject.status !== 'completed' && subject.status !== 'denied'

    switch (filter) {
      case 'in_progress':
        return subject.status === 'in_progress'

      case 'completed':
        return subject.status === 'completed'

      case 'pending_verification':
        return subject.verification_status === 'pending'

      case 'due_soon': {
        if (!isActive || !subject.due_date) return false
        const dueDate = new Date(subject.due_date)
        return dueDate > now && dueDate <= sevenDaysFromNow
      }

      case 'overdue': {
        if (!isActive || !subject.due_date) return false
        const dueDate = new Date(subject.due_date)
        return dueDate < now
      }

      default:
        return true
    }
  })
}

export function PrivacyListPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [viewingSubject, setViewingSubject] = useState<PrivacySubject | null>(null)
  const [editingSubject, setEditingSubject] = useState<PrivacySubject | null>(null)
  const [deletingSubject, setDeletingSubject] = useState<PrivacySubject | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  // Get filter from URL, default to 'all'
  const currentFilter = (searchParams.get('filter') as FilterType) || 'all'

  const { data, isLoading, error, refetch, isRefetching } = usePrivacyListAll()
  const createMutation = useCreatePrivacy()
  const updateMutation = useUpdatePrivacy()
  const deleteMutation = useDeletePrivacy()
  const { toast } = useToast()

  // Apply filter
  const filteredSubjects = useMemo(
    () => filterSubjects(data?.subjects || [], currentFilter),
    [data?.subjects, currentFilter]
  )

  // Handle filter change
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFilter = e.target.value as FilterType
    if (newFilter === 'all') {
      searchParams.delete('filter')
    } else {
      searchParams.set('filter', newFilter)
    }
    setSearchParams(searchParams)
  }

  const handleCreate = async (formData: CreatePrivacyFormData) => {
    try {
      await createMutation.mutateAsync({
        // Only send non-empty strings to avoid backend validation errors
        email: formData.email?.trim() || undefined,
        phone: formData.phone?.trim() || undefined,
        first_name: formData.first_name?.trim() || undefined,
        last_name: formData.last_name?.trim() || undefined,
        opt_out_sale: formData.opt_out_sale,
        opt_out_share: formData.opt_out_share,
        remove_data: formData.remove_data,
        do_not_contact: formData.do_not_contact,
        jurisdiction: formData.jurisdiction,
        received_at: formData.received_at?.trim() || undefined,
        source: formData.source?.trim() || undefined,
        notes: formData.notes?.trim() || undefined,
      })
      toast({ title: 'Success', description: 'Privacy subject created successfully', variant: 'success' })
      setIsCreateModalOpen(false)
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create privacy subject',
        variant: 'error',
      })
    }
  }

  const handleUpdate = async (formData: UpdatePrivacyFormData) => {
    if (!editingSubject) return

    try {
      await updateMutation.mutateAsync({
        id: editingSubject.id,
        request: {
          phone: formData.phone?.trim() || undefined,
          first_name: formData.first_name?.trim() || undefined,
          last_name: formData.last_name?.trim() || undefined,
          opt_out_sale: formData.opt_out_sale,
          opt_out_share: formData.opt_out_share,
          remove_data: formData.remove_data,
          do_not_contact: formData.do_not_contact,
          verification_status: formData.verification_status,
          status: formData.status,
          notes: formData.notes?.trim() || undefined,
        },
      })
      toast({ title: 'Success', description: 'Privacy subject updated successfully', variant: 'success' })
      setEditingSubject(null)
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update privacy subject',
        variant: 'error',
      })
    }
  }

  const handleDelete = async () => {
    if (!deletingSubject) return

    try {
      await deleteMutation.mutateAsync(deletingSubject.id)
      toast({ title: 'Success', description: 'Privacy subject deleted successfully', variant: 'success' })
      setDeletingSubject(null)
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete privacy subject',
        variant: 'error',
      })
    }
  }

  const handleView = (subject: PrivacySubject) => {
    setViewingSubject(subject)
  }

  const handleEdit = (subject: PrivacySubject) => {
    setViewingSubject(null)
    setEditingSubject(subject)
  }

  if (isLoading) {
    return (
      <PageLayout title="Privacy Subjects" description="Manage data privacy requests and subjects">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout title="Privacy Subjects" description="Manage data privacy requests and subjects">
        <ErrorDisplay
          error={error as Error}
          title="Failed to Load Privacy Subjects"
          onRetry={() => refetch()}
        />
      </PageLayout>
    )
  }

  // Build description with filter context
  const filterLabel = FILTER_OPTIONS.find((f) => f.value === currentFilter)?.label || 'All'
  const description =
    currentFilter === 'all'
      ? 'Manage data privacy requests and subjects'
      : `Showing ${filteredSubjects.length} ${filterLabel.toLowerCase()} subject${filteredSubjects.length !== 1 ? 's' : ''}`

  // Filter dropdown for table toolbar
  const filterDropdown = (
    <Select
      options={FILTER_OPTIONS}
      value={currentFilter}
      onChange={handleFilterChange}
      className="w-44 text-sm"
    />
  )

  return (
    <PageLayout
      title="Privacy Subjects"
      description={description}
      actions={
        <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Subject
        </Button>
      }
    >
      <PrivacyTable
        data={filteredSubjects}
        onView={handleView}
        onRefresh={() => refetch()}
        isRefreshing={isRefetching}
        toolbarLeft={filterDropdown}
      />

      {/* View Detail Modal */}
      <PrivacyDetailModal
        subject={viewingSubject}
        isOpen={!!viewingSubject}
        onClose={() => setViewingSubject(null)}
        onEdit={handleEdit}
        onDelete={(subject) => {
          setViewingSubject(null)
          setDeletingSubject(subject)
        }}
      />

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Add Privacy Subject"
        size="lg"
      >
        <PrivacyForm
          mode="create"
          onSubmit={handleCreate}
          onCancel={() => setIsCreateModalOpen(false)}
          isSubmitting={createMutation.isPending}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingSubject}
        onClose={() => {
          setViewingSubject(editingSubject)
          setEditingSubject(null)
        }}
        title="Edit Privacy Subject"
        size="lg"
      >
        {editingSubject && (
          <PrivacyForm
            mode="edit"
            initialData={editingSubject}
            onSubmit={handleUpdate}
            onCancel={() => {
              setViewingSubject(editingSubject)
              setEditingSubject(null)
            }}
            onDelete={() => {
              setEditingSubject(null)
              setDeletingSubject(editingSubject)
            }}
            isSubmitting={updateMutation.isPending}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deletingSubject}
        onClose={() => setDeletingSubject(null)}
        onConfirm={handleDelete}
        title="Delete Privacy Subject"
        description={`Are you sure you want to delete the privacy subject for "${deletingSubject?.email || deletingSubject?.phone || 'this record'}"? This action cannot be undone.`}
        confirmText="Delete"
        loading={deleteMutation.isPending}
        variant="danger"
      />
    </PageLayout>
  )
}
